import { put, select, call, delay } from 'redux-saga/effects'
import SigningActions from '../reducers/signingRedux'
import WorkflowActions from '../reducers/workflowRedux'
import { loadAuthState } from './authorizeCRASagas'

// Adobe can report completion before the final signed PDF is retrievable.
// Keep retrying long enough for eventual consistency on provider side.
const MAX_COMPLETE_RETRIES = 90
const COMPLETE_RETRY_DELAY_MS = 2000

const getSigning = state => state.signing

export function* getSigningUrl(api, action) {
    var signing = yield select(getSigning)


    var response = null;
    if (signing.isAdditional) {
        response = yield call(api.getESignatureUriForAdditionalDocs, action.taxYear, signing.forms, signing.isSpouse)
    } else {
        response = yield call(api.getESignatureUri, action.taxYear, signing.forms, signing.externalUrls, signing.isSpouse)
    }
    if (response && response.ok && response.data) {
        var { signingUrl, envelopeId, recipientId } = response.data
        // Persist callback context as soon as we get it from API so it survives
        // Adobe full-page redirects even if React lifecycle timing changes.
        if (envelopeId && recipientId) {
            sessionStorage.setItem('signingEnvelopeId', envelopeId)
            sessionStorage.setItem('signingRecipientId', recipientId)
            if (signing && signing.signingCompleteRoute) {
                sessionStorage.setItem('signingCompleteRoute', signing.signingCompleteRoute)
            }
        }
        yield put(SigningActions.setSigningUri(signingUrl, envelopeId, recipientId))
    } else {
        yield put(SigningActions.completeFormSigning('failed'))
        // VAI-476 check for specific error message indicating that the file to be signed was not found in Box.
        if (response && response.status === 404) {
            yield put(SigningActions.setSigningError('filenotfound'));
        }
    }
}

export function* logoutResetSigningState(action) {
    yield put(SigningActions.signingStateLogoutReset())
}

export function* processDocumentCallback(api, authorizeCRAApi, action) {
    if (action.status === 'in_progress') {
        var signing = yield select(getSigning)
        var initialEnvelopeId = signing && signing.envelopeId ? signing.envelopeId : sessionStorage.getItem('signingEnvelopeId')
        var initialRecipientId = signing && signing.recipientId ? signing.recipientId : sessionStorage.getItem('signingRecipientId')

        if (initialEnvelopeId) {
            try {
                yield put(SigningActions.completeFormSigning('processing'))
                var response = null
                for (var attempt = 1; attempt <= MAX_COMPLETE_RETRIES; attempt++) {
                    signing = yield select(getSigning)
                    var envelopeId = signing && signing.envelopeId ? signing.envelopeId : sessionStorage.getItem('signingEnvelopeId')
                    var recipientId = signing && signing.recipientId ? signing.recipientId : sessionStorage.getItem('signingRecipientId')
                    if (!envelopeId) {
                        if (attempt < MAX_COMPLETE_RETRIES) {
                            yield delay(COMPLETE_RETRY_DELAY_MS)
                            continue
                        }
                        break
                    }

                    response = yield call(api.completeForm, envelopeId, recipientId || null)

                    if (response && response.ok) {
                        break
                    }

                    if (response && (response.status === 401 || response.status === 403)) {
                        yield call(loadAuthState, authorizeCRAApi, {})
                    }

                    if (attempt < MAX_COMPLETE_RETRIES) {
                        yield delay(COMPLETE_RETRY_DELAY_MS)
                    }
                }
                
                if (response && response.ok) {
                    if (signing.isAdditional) {
                        yield put(WorkflowActions.updateCaseState())
                    }                
                    yield call(loadAuthState, authorizeCRAApi, {})
                    yield call(getSignedDocuments, api, action)                
                    signing = yield select(getSigning)
                    var signedDocs = signing.signedDocuments
                    var forms = Array.isArray(signing.forms) ? signing.forms : []
                    var updatedSignedDocs = null;
                    var i = 0;
                    if (signing.isSpouse) {
                        var spouse = Object.assign({}, signedDocs.spouse)
                        for (i = 0; i < forms.length; ++i) {
                            spouse[forms[i]] = true
                        }
                        updatedSignedDocs = Object.assign({}, signedDocs, { spouse: spouse })
                    } else {
                        var self = Object.assign({}, signedDocs.self)
                        for (i = 0; i < forms.length; ++i) {
                            self[forms[i]] = true
                        }
                        updatedSignedDocs = Object.assign({}, signedDocs, { self: self })
                    }
                    yield put(SigningActions.setSignedDocuments(updatedSignedDocs))                
                    yield put(SigningActions.completeFormSigning('complete'))
                    yield put(SigningActions.processDocumentCallback('complete'))
                } else {
                    yield put(SigningActions.completeFormSigning('failed'))
                    yield put(SigningActions.processDocumentCallback('failed'))
                }
            } catch (error) {
                // Treat callback errors as failed so review flow does not advance prematurely.
                yield put(SigningActions.completeFormSigning('failed'))
                yield put(SigningActions.processDocumentCallback('failed'))
            }
        } else {
            // Missing signing context means we cannot confirm completion.
            // Mark as failed to avoid falsely showing "Thanks for reviewing".
            yield put(SigningActions.completeFormSigning('failed'))
            yield put(SigningActions.processDocumentCallback('failed'))
        }
    }
    else if (action.status === 'complete') {
        // OTE-320 retrieve updated case state from the server.
        yield put(WorkflowActions.updateCaseState())
    }
}

export function* getSignedDocuments(api, action) {
    var response = yield call(api.getSignedForms)
    if (response && response.ok) {
        yield put(SigningActions.setSignedDocuments(response.data))
    }
}

export function* checkSigningState(api, action) {
    // we just loaded the signing page, if this was from hitting back, reset this
    var signing = yield select(getSigning)
    if (signing && signing.signingState === 'in_progress') {
        yield put(SigningActions.completeFormSigning('failed'))
    }
}

export function* cancelSigningSaga(action) {
    // User cancelled signing (closed popup or pressed back button)
    // Simply reset the signing state - the reducer already handles this
    // No navigation needed here as the user is already on the originating page
    yield put(SigningActions.signingStateReset())
}
